import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MessageSquareText } from "lucide-react";

import TicketsManager from "../../components/TicketsManager/";
import Ticket from "../../components/Ticket/";
import useLayoutTickets from "../../hooks/useLayoutTickets";
import usePrefTickets from "../../hooks/usePrefTickets";
import useAtalhoDeTeclado from "../../hooks/useAtalhoDeTeclado";
import AjudaDeAtalhos from "../../components/AjudaDeAtalhos";
import useLarguraArrastavel from "../../hooks/useLarguraArrastavel";
import { linhasDaListaVisivel } from "../../helpers/linhasDaListaVisivel";

import { i18n } from "../../translate/i18n";

const Chat = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const layout = useLayoutTickets();

  const foco = layout === "foco";
  const painel = layout === "painel";
  // Largura da coluna da lista no Clássico. É a única em que arrastar faz
  // sentido: no Foco a lista ocupa tudo ou nada, e no Painel a largura é
  // proporcional porque a grade precisa de espaço para as colunas.
  const classico = !foco && !painel;
  // Canhoto, segunda tela à esquerda, gosto: a lista pode ficar do outro lado.
  // É só a ordem dos dois blocos — nada dentro deles muda.
  const [ladoDaLista] = usePrefTickets("ladoDaLista", "esquerda");

  // Largura da coluna da lista no Clássico. No Painel ela é proporcional
  // (a grade precisa de espaço para as colunas) e no Foco a lista ocupa tudo
  // ou nada — nos dois não há divisa para arrastar.
  const {
    ref: refDaLista,
    largura: larguraDaLista,
    ativo: larguraDaListaVale,
    iniciarArrasto: arrastarLista,
  } = useLarguraArrastavel({
    chave: "larguraListaClassico",
    padrao: 340,
    min: 280,
    max: () => Math.min(560, window.innerWidth * 0.6),
    invertido: ladoDaLista === "direita",
  });

  // Largura da conversa no Foco. Ali ela ocupa a tela inteira, e em monitor
  // largo a linha de texto fica comprida demais para ler — o que se arrasta
  // aqui é até onde a conversa vai, com o resto ficando de margem.
  const {
    ref: refDaConversa,
    largura: larguraDaConversa,
    ativo: larguraDaConversaVale,
    iniciarArrasto: arrastarConversa,
  } = useLarguraArrastavel({
    chave: "larguraConversaFoco",
    padrao: 1100,
    min: 600,
    max: () => window.innerWidth,
  });

  // No layout Foco a lista sai da tela ao abrir a conversa, então precisa
  // haver um caminho de volta que não seja só o botão: Esc devolve a lista.
  useAtalhoDeTeclado(
    "Escape",
    () => navigate("/tickets"),
    foco && Boolean(ticketId),
  );

  // Andar na lista sem tirar a mão do teclado. A ordem vem do DOM porque é a
  // única que já tem aba, filtro e busca aplicados — refazer a conta aqui
  // seria inventar uma segunda lista, que uma hora discorda da que está na
  // tela.
  const irPara = (passo) => {
    const linhas = linhasDaListaVisivel();
    if (!linhas.length) return;

    const atual = linhas.findIndex(
      (l) => l.dataset.ticketId === String(ticketId),
    );
    // Sem conversa aberta, "próximo" é a primeira da lista.
    const alvo = linhas[atual < 0 ? 0 : atual + passo];

    if (alvo) navigate(`/tickets/${alvo.dataset.ticketId}`);
  };

  useAtalhoDeTeclado("j", () => irPara(1));
  useAtalhoDeTeclado("k", () => irPara(-1));

  // A busca da lista é do TicketsManager; daqui só se põe o foco nela.
  useAtalhoDeTeclado("/", () => {
    const busca = document.querySelector(
      '[data-busca-da-lista], [aria-label="Buscar atendimento e mensagens"]',
    );
    busca?.focus();
  });

  // Larguras da coluna da lista, por layout:
  // - clássico: coluna fixa ao lado da conversa;
  // - painel: a grade precisa de espaço para as colunas (espera, fila, dono);
  // - foco: a lista ocupa tudo quando não há conversa aberta, e some quando há.
  //
  // `@container`: o trilho de pastas da lista (navegação lateral) mostra os
  // nomes ou só os ícones conforme a largura DESTA coluna, não a da janela.
  const colunaDaLista = foco
    ? ticketId
      ? "hidden"
      : "@container block w-full"
    : [
        "@container relative h-full w-full shrink-0 overflow-hidden border-r",
        // No clássico quem manda é o estilo inline (a largura salva); só o
        // painel continua com largura de classe.
        painel ? "lg:w-[46%] xl:w-[52%]" : "lg:w-[340px] xl:w-[380px]",
        // lg: e não sm: — em 768px a lista de 320px, mais a lateral, deixava
        // 184px para a conversa. Uma coluna por vez até 1024; as duas juntas
        // só quando há largura de verdade para as duas.
        ticketId ? "hidden lg:block" : "block",
      ].join(" ");

  return (
    <div
      className={`relative flex h-full min-h-0 flex-1 overflow-hidden ${
        ladoDaLista === "direita" ? "flex-row-reverse" : ""
      }`}
    >
      <div
        ref={refDaLista}
        className={colunaDaLista}
        style={
          classico && larguraDaListaVale ? { width: larguraDaLista } : undefined
        }
      >
        <TicketsManager />
        {classico && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Arraste para mudar a largura da lista"
            title="Arraste para mudar a largura da lista"
            onPointerDown={arrastarLista}
            className={`absolute inset-y-0 z-10 hidden w-1.5 cursor-col-resize touch-none transition-colors hover:bg-primary/30 active:bg-primary/40 lg:block ${
              ladoDaLista === "direita" ? "left-0" : "right-0"
            }`}
          />
        )}
      </div>
      {/* Uma conversa só, nunca duas.
          Antes havia uma cópia para desktop e outra para celular, e com um
          atendimento aberto as DUAS montavam: dois sockets na mesma sala,
          duas buscas do ticket e duas da lista de mensagens a cada abertura.
          Quem decide onde ela aparece é o CSS, não um segundo <Ticket/>.

          min-w-0: item flex nasce com min-width:auto e se recusa a encolher
          além do conteúdo. Sem isso a conversa estica até a largura da maior
          mensagem e vaza para fora da tela — no celular os balões apareciam
          cortados pela direita, com o texto inalcançável. */}
      <div
        ref={refDaConversa}
        // `flex-1` sai quando a largura é arrastada: item flex com
        // `flex: 1 1 0%` ignora a largura pedida e ocupa a sobra — o valor
        // ficava no style e a conversa continuava esticada até a borda.
        className={`relative min-h-0 min-w-0 ${
          foco && ticketId && larguraDaConversaVale ? "mx-auto shrink-0" : "flex-1"
        } ${ticketId ? "flex" : foco ? "hidden" : "hidden lg:flex"}`}
        style={
          foco && ticketId && larguraDaConversaVale
            ? { width: larguraDaConversa, maxWidth: "100%" }
            : undefined
        }
      >
        {ticketId ? (
          <>
            <Ticket />
            {/* Só no Foco: no Clássico a divisa que interessa é a da lista, e
                duas bordas arrastáveis lado a lado só confundem. */}
            {foco && (
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Arraste para mudar a largura da conversa"
                title="Arraste para mudar a largura da conversa"
                onPointerDown={arrastarConversa}
                className="absolute inset-y-0 right-0 z-10 hidden w-1.5 cursor-col-resize touch-none transition-colors hover:bg-primary/30 active:bg-primary/40 lg:block"
              />
            )}
          </>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-muted/30 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <MessageSquareText className="h-7 w-7" />
            </div>
            <span className="max-w-xs text-sm text-muted-foreground">
              {i18n.t("chat.noTicketMessage")}
            </span>
          </div>
        )}
      </div>
      {/* Com a conversa aberta a ajuda vive no cabeçalho do atendimento, com
          as outras ações: boiando na lateral ela roubava faixa da lista. */}
      {!ticketId && <AjudaDeAtalhos foco={foco} />}
    </div>
  );
};

export default Chat;
